import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { AppDataState } from '../../../../common/state-stores/app-data-state.state';
import { RequestUtil } from '../../../../common/util/request-util';
import { ServiceErrorUtil } from '../../../../common/util/service-error.util';
import { PaymentTransaction } from '../../interfaces/payment-transaction';
import {
  PointAndPayConfiguration,
  PointAndPayInitiateRequest,
  PointAndPayInitiateResponse,
} from '../../interfaces/pointandpay-payment';

@Injectable({ providedIn: 'root' })
export class PointAndPayPaymentService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly appState: AppDataState,
  ) {}

  async initiatePayment(request: PointAndPayInitiateRequest): Promise<PointAndPayInitiateResponse> {
    const requestId = RequestUtil.generateRequestId();
    try {
      const response = await lastValueFrom(
        this.httpClient.post<PointAndPayInitiateResponse>(
          `${environment.baseBackendUrl}payment/pointandpay/initiate`,
          request,
          { headers: this.buildHeaders(requestId) },
        ),
      );
      if (!response?.paymentUrl || !response.paramXml) {
        throw new Error('PointAndPay returned an incomplete payment request.');
      }
      return response;
    } catch (error: any) {
      throw ServiceErrorUtil.createServiceError(
        this.constructor.name,
        error.message || 'Unable to initiate PointAndPay payment. Please try again.',
        requestId,
      );
    }
  }

  async getConfiguration(): Promise<PointAndPayConfiguration> {
    const requestId = RequestUtil.generateRequestId();
    try {
      return await lastValueFrom(
        this.httpClient.get<PointAndPayConfiguration>(
          `${environment.baseBackendUrl}payment/pointandpay/configuration`,
          { headers: this.buildHeaders(requestId) },
        ),
      );
    } catch (error: any) {
      throw ServiceErrorUtil.createServiceError(this.constructor.name, error.message || 'Unable to load PointAndPay configuration.', requestId);
    }
  }

  async saveConfiguration(configuration: PointAndPayConfiguration): Promise<PointAndPayConfiguration> {
    const requestId = RequestUtil.generateRequestId();
    try {
      return await lastValueFrom(
        this.httpClient.post<PointAndPayConfiguration>(
          `${environment.baseBackendUrl}payment/pointandpay/configuration`,
          configuration,
          { headers: this.buildHeaders(requestId) },
        ),
      );
    } catch (error: any) {
      throw ServiceErrorUtil.createServiceError(this.constructor.name, error.message || 'Unable to save PointAndPay configuration.', requestId);
    }
  }

  async getTransactions(): Promise<PaymentTransaction[]> {
    const requestId = RequestUtil.generateRequestId();
    try {
      return await lastValueFrom(
        this.httpClient.get<PaymentTransaction[]>(
          `${environment.baseBackendUrl}payment/pointandpay/transactions`,
          { headers: this.buildHeaders(requestId) },
        ),
      );
    } catch (error: any) {
      throw ServiceErrorUtil.createServiceError(this.constructor.name, error.message || 'Unable to load PointAndPay transactions.', requestId);
    }
  }

  private buildHeaders(requestId: string): Record<string, string> {
    return {
      'App-Id': this.appState.getAppInstanceId().toString(),
      'Operator-Id': this.appState.getOperatorId().toString(),
      'PermissionSet-Id': this.appState.getPermissionSetId().toString(),
      'Request-Id': requestId,
    };
  }
}
